import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'

interface TwinSummary {
  twin_id: string
  respondent_external_id: string
  segment_id?: string
  segment_name?: string
  response_count: number
  open_end_count: number
  build_status: string
  best_accuracy?: number
  wilson_lower?: number
  wilson_upper?: number
  excluded_reason?: string
  questions_tested?: number
  questions_abstained?: number
}

function statusVariant(status: string): 'success' | 'warning' | 'default' {
  if (status === 'validated') return 'success'
  if (status === 'low_confidence') return 'warning'
  return 'default'
}

function statusLabel(status: string): string {
  if (status === 'validated') return 'Validated'
  if (status === 'low_confidence') return 'Low confidence'
  if (status === 'excluded') return 'Excluded'
  return status
}

const DEMO_TWINS: TwinSummary[] = Array.from({ length: 12 }, (_, i) => ({
  twin_id: `twin_demo_${String(i + 1).padStart(4, '0')}`,
  respondent_external_id: `respondent_${String(i + 1).padStart(4, '0')}`,
  segment_id: i < 8 ? 'seg_general' : i < 10 ? 'seg_high_engagement' : 'seg_recent_opt_outs',
  segment_name: i < 8 ? 'General customers' : i < 10 ? 'High-engagement / under-35' : 'Recent opt-outs',
  response_count: 8 + (i % 5),
  open_end_count: 2 + (i % 3),
  build_status: i < 9 ? 'validated' : i < 11 ? 'low_confidence' : 'excluded',
  best_accuracy: i < 9 ? 0.65 + (i * 0.03) : i < 11 ? 0.38 : undefined,
  wilson_lower: i < 9 ? 0.58 + (i * 0.02) : i < 11 ? 0.28 : undefined,
  wilson_upper: i < 9 ? 0.72 + (i * 0.02) : i < 11 ? 0.48 : undefined,
  excluded_reason: i >= 11 ? 'too_few_prediction_responses' : undefined,
}))

export function TwinsListPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [twins, setTwins] = useState<TwinSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'validated' | 'low_confidence' | 'excluded'>('all')
  const [search, setSearch] = useState('')

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    if (isDemo) { setTwins(DEMO_TWINS); setLoading(false); return }
    fetch(`/api/v1/projects/${projectId}/twin-panel/twins`, { headers })
      .then(r => r.ok ? r.json() as Promise<{ twins: TwinSummary[] }> : { twins: [] }).catch(() => ({ twins: [] }))
      .then(d => setTwins(d.twins ?? []))
      .finally(() => setLoading(false))
  }, [projectId, isDemo])

  const filtered = twins.filter(t => {
    const matchesFilter = filter === 'all' || t.build_status === filter
    const matchesSearch = !search || t.respondent_external_id.toLowerCase().includes(search.toLowerCase())
      || (t.segment_name ?? '').toLowerCase().includes(search.toLowerCase())
    return matchesFilter && matchesSearch
  })

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading twins...</div>

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-medium text-deep-ink">Individual twins</h1>
          <p className="text-sm text-slate-500 mt-0.5">{twins.length.toLocaleString()} twins built from uploaded respondents</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.75} />
          <input
            className="pl-9 pr-3 py-2 text-sm border border-border-gray rounded-lg focus:outline-none focus:ring-1 focus:ring-echo-indigo w-64"
            placeholder="Search respondent ID or segment..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="primary-cta-zone">
        {(['all', 'validated', 'low_confidence', 'excluded'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
              filter === f ? 'bg-echo-indigo text-white' : 'text-slate-500 hover:text-deep-ink hover:bg-soft-panel'
            }`}
            data-testid={f === 'validated' ? 'primary-cta' : undefined}
          >
            {f === 'all' ? 'All' : statusLabel(f)} ({twins.filter(t => f === 'all' || t.build_status === f).length})
          </button>
        ))}
      </div>

      {/* Twins list */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-sm text-slate-400 py-8 text-center">No twins match this filter.</div>
        )}
        {filtered.map(twin => (
          <Link
            key={twin.twin_id}
            to={`/app/projects/${projectId ?? 'demo'}/twin-panel/twins/${twin.twin_id}`}
            className="block"
          >
            <Card className="hover:border-echo-indigo/30 transition-colors cursor-pointer">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-deep-ink">{twin.respondent_external_id}</span>
                    <Badge variant={statusVariant(twin.build_status)} size="sm">{statusLabel(twin.build_status)}</Badge>
                    {twin.excluded_reason && (
                      <span className="text-xs text-slate-400">{twin.excluded_reason.replace(/_/g, ' ')}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                    {twin.segment_name && <span>Segment: {twin.segment_name}</span>}
                    <span>{twin.response_count} responses · {twin.open_end_count} open-ends</span>
                    {twin.best_accuracy != null && (
                      <span>
                        Accuracy: <strong className="text-slate-600">{(twin.best_accuracy * 100).toFixed(0)}%</strong>
                        {twin.wilson_lower != null && twin.wilson_upper != null && (
                          <span className="ml-1 text-slate-400 font-mono">
                            ({(twin.wilson_lower * 100).toFixed(0)}–{(twin.wilson_upper * 100).toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    )}
                    {twin.questions_tested != null && twin.questions_tested > 0 && (
                      <span className="text-slate-400" data-testid="scored-denominator">
                        scored {twin.questions_tested} of {twin.questions_tested + (twin.questions_abstained ?? 0)}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs text-echo-indigo shrink-0">Inspect →</span>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}
